import { useMemo } from "react";
import { Tooltip } from "flowbite-react";
import { useTranslation } from "@repo/core";
import { HiInformationCircle } from "react-icons/hi";
import type {
  PerformanceChartData,
  PortfolioType,
} from "@repo/api";
import { formatPercentage, formatRatio } from "../../../utils";

const AttributesHeader = ({
  chartData,
  portfolio,
}: {
  chartData?: PerformanceChartData;
  portfolio: PortfolioType;
}) => {
  const { tCommon, tStrategy } = useTranslation();
  const actualValue = useMemo(() => {
    if (!chartData?.Strategy) return 0;

    const strategyData = chartData.Strategy;
    const dates = Object.keys(strategyData).sort();

    if (dates.length === 0) return 0;

    const lastDate = dates[dates.length - 1];
    const lastValue = strategyData[lastDate];

    // Validate that we have a valid number
    if (typeof lastValue !== "number" || isNaN(lastValue)) return 0;

    // Assuming the chart data represents cumulative returns, convert to dollar value
    // If strategy has initial cash, use that as base, otherwise use a default
    const initialCash = portfolio?.backtest?.startCash || 100000; // Default $100k

    // If chart values are already in dollar amounts, return as is
    // If they are percentages, convert to dollar value
    if (lastValue > 1000) {
      // Likely already in dollar amounts
      return Math.round(lastValue);
    } else {
      // Likely percentage returns, convert to dollar value
      return Math.round(initialCash * (1 + lastValue / 100));
    }
  }, [chartData?.Strategy, portfolio?.backtest?.startCash]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
      {/* Actual Value */}
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-600">
            {tStrategy("actualValue")}
          </span>
          <Tooltip content={tStrategy("tooltip.actualValue")} placement="top">
            <HiInformationCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </div>
        <div className="text-xl font-bold text-gray-900">
          ${(actualValue || 0).toLocaleString()}
        </div>
      </div>

      {/* Total Return */}
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-600">{tCommon("CAGR")}</span>
          <Tooltip content={tStrategy("tooltip.totalReturn")} placement="top">
            <HiInformationCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`text-xl font-bold ${
              portfolio.CAGR >= 0 ? "text-green-600" : "text-red-600"
            }`}
          >
            {portfolio.CAGR >= 0 ? "+" : ""}
            {formatRatio(portfolio.CAGR * 100).toLocaleString()}% p.a.
          </div>
        </div>
      </div>

      {/* Sharpe Ratio */}
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-600">
            {tStrategy("sharpeRatio")}
          </span>
          <Tooltip content={tStrategy("tooltip.sharpeRatio")} placement="top">
            <HiInformationCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </div>
        <div className="text-xl font-bold text-gray-900">
          {formatRatio(portfolio.Sharpe, 2)}
        </div>
      </div>

      {/* Volatility */}
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-600">
            {tStrategy("volatility")}
          </span>
          <Tooltip content={tStrategy("tooltip.volatility")} placement="top">
            <HiInformationCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </div>
        <div className="text-xl font-bold text-gray-900">
          {formatPercentage(portfolio.Volatility, 2)} p.a.
        </div>
      </div>

      {/* Turnover */}
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-600">
            {tStrategy("turnover")}
          </span>
          <Tooltip content={tStrategy("tooltip.turnover")} placement="top">
            <HiInformationCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </div>
        <div className="text-xl font-bold text-gray-900">
          {formatPercentage(portfolio.meanTurnover, 2)}
        </div>
      </div>
    </div>
  );
};

export default AttributesHeader;
